import React from "react";
import { useTable, useSortBy, useGroupBy, useExpanded, usePagination, useFilters } from "react-table";
import { SvgIcon } from ".";
import { Container, Spinner } from "react-bootstrap";
import { ControlledPagination } from "./ControlledPagination";
import ControlledTableContext from "./ControlledTableContext";

const defaultPropGetter = () => ({});

const ControlledTable = React.forwardRef(
  (
    {
      columns,
      data,
      getHeaderProps = defaultPropGetter,
      getColumnProps = defaultPropGetter,
      getRowProps = defaultPropGetter,
      getCellProps = defaultPropGetter,
      hiddenColumns = [],
      fetchData,
      isLoading = false,
      totalCount,
      totalPageCount,
      previous,
      next,
      selectedItems,
      groupBy = []
    },
    ref
  ) => {
    const instance = useTable(
      {
        columns,
        data,
        initialState: { pageIndex: 0, groupBy: groupBy },
        manualPagination: true,
        manualFilters: true,
        selectedItems,
        pageCount: totalPageCount
      },
      useFilters,
      useGroupBy,
      useSortBy,
      useExpanded,
      usePagination
    );
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      setHiddenColumns,
      page,
      gotoPage,
      setPageSize,
      toggleAllRowsExpanded,
      state: { pageSize, filters }
    } = instance;

    React.useImperativeHandle(ref, () => instance);

    const { currentPage, setCurrentPage } = React.useContext(ControlledTableContext);
    const [start, setStart] = React.useState(1);
    const [end, setEnd] = React.useState(totalCount < pageSize ? totalCount : pageSize);

    const fetch = async () => {
      const data = await fetchData({ page: currentPage, per: pageSize, filters });
      if (data?.data?.length > 0 && groupBy.length > 0) {
        toggleAllRowsExpanded(true);
      }
    };

    React.useEffect(() => {
      setHiddenColumns([columns[0].id, ...hiddenColumns]);
    }, [columns]);

    React.useEffect(() => {
      fetch();
    }, [fetchData, currentPage, pageSize, filters]);

    const loader = () => {
      return (
        <Container fluid={true}>
          <div className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" role="status" variant="brand">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      );
    };

    return (
      <div className="dt-bootstrap5 no-footer">
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <div className="dataTables_length" id="projects-datatable_length">
              <label>
                Show{" "}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    gotoPage(0);
                    setStart(1);
                    setEnd(totalCount < Number(e.target.value) ? totalCount : Number(e.target.value));

                    if (currentPage > 1) setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
                entries
              </label>
            </div>
          </div>
          <div className="col-sm-12 col-md-6">
            <div id="projects-datatable_filter" className="dataTables_filter"></div>
          </div>
        </div>
        {isLoading ? (
          loader()
        ) : (
          <table {...getTableProps()} className="datatables table table--striped table--condensed">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      className="text-center text-bold pointer"
                      {...column.getHeaderProps([
                        {
                          className: column.className,
                          style: column.style
                        },
                        getColumnProps(column),
                        getHeaderProps(column),
                        column.getSortByToggleProps()
                      ])}
                    >
                      {column.render("Header")}
                      {"\t"}
                      {column.isSorted && (column.isSortedDesc ? <SvgIcon name="chevron-up" /> : <SvgIcon name="chevron-down" />)}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps(getRowProps(row))}>
                    {row.cells.map((cell) => {
                      return (
                        <td
                          {...cell.getCellProps([
                            {
                              className: cell.column.className,
                              style: cell.column.style
                            },
                            getColumnProps(cell.column),
                            getCellProps(cell)
                          ])}
                        >
                          {cell.isGrouped ? (
                            <>
                              <div className="d-flex" {...row.getToggleRowExpandedProps()}>
                                {row.isExpanded ? <SvgIcon name="chevron-down" className="mr-1" /> : <SvgIcon name="chevron-up" className="mr-1" />}
                                {cell.render("Cell")}
                              </div>
                            </>
                          ) : cell.isAggregated ? (
                            cell.render("Aggregated")
                          ) : cell.isPlaceholder ? null : (
                            cell.render("Cell")
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="row mt-2">
          <div className="col-sm-12 col-md-5">
            <div className="dataTables_info" aria-live="polite">
              {data.length > 0
                ? `Showing ${start} to ${end} of ${totalCount} entries`
                : `No entries to show. (filtered from ${totalCount} total entries)`}
            </div>
          </div>
          <div className="col-sm-12 col-md-7">
            <div className="dataTables_paginate paging_simple_numbers">
              <ControlledPagination
                totalRecords={totalCount}
                pageSize={pageSize}
                pageNeighbours={1}
                gotoPage={gotoPage}
                canPreviousPage={!!previous}
                canNextPage={!!next}
                setStart={setStart}
                setEnd={setEnd}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default ControlledTable;
